/**
 * The one approval policy for planned work: which task approval standings count as approved for each
 * purpose, and how a standing is named and coloured. The web tags, the API metadata, the stamper and Job
 * construction all ask here, so a standing cannot mean one thing on a board and another to the builder.
 */
import { JobConstructionAuthorisationKind } from './job.construction.evidence';
import { TaskApprovalStatus, TaskApprovalSummaryRow } from './task.approval';
import { isApprovalCurrent } from './task.approval.summary';
import type { MetadataEnumTypeData } from '../../../metadata/entity/decorators/model/metadata.entity.field.data';

/**
 * Semantic colour of an approval standing. The names are the web's `StatusTone` members, so a web tag
 * uses them as-is; API metadata maps them to badge severities through `TASK_APPROVAL_OPTIONS`.
 */
export type TaskApprovalTone = 'neutral' | 'warning' | 'brand' | 'success';

type Standing = TaskApprovalStatus | null | undefined;

/** The `isFullyApproved` standings as filter values, for the board views and tiles that select "Approved". */
export const FULLY_APPROVED_STATUSES: TaskApprovalStatus[] = [TaskApprovalStatus.Approved, TaskApprovalStatus.AutoApproved];

const FULLY_APPROVED: ReadonlySet<Standing> = new Set(FULLY_APPROVED_STATUSES);

const BUILDS_JOB: ReadonlySet<Standing> = new Set([TaskApprovalStatus.Approved, TaskApprovalStatus.PartlyApproved, TaskApprovalStatus.AutoApproved]);

const BUILDS_ON_PERSON_APPROVAL: ReadonlySet<Standing> = new Set([TaskApprovalStatus.Approved, TaskApprovalStatus.PartlyApproved]);

const HELD_BY_PERSON: ReadonlySet<Standing> = new Set([TaskApprovalStatus.Approved, TaskApprovalStatus.PartlyApproved, TaskApprovalStatus.NeedsReview]);

/**
 * Short enough for a table cell. "Changed" is NeedsReview: an approval that went stale and needs approving
 * again; `approvalTooltip` spells it out.
 */
const LABELS: Record<TaskApprovalStatus, string> = {
    [TaskApprovalStatus.Approved]: 'Approved',
    [TaskApprovalStatus.AutoApproved]: 'Auto-approved',
    [TaskApprovalStatus.PartlyApproved]: 'Partly approved',
    [TaskApprovalStatus.NeedsReview]: 'Changed',
    [TaskApprovalStatus.NotApproved]: 'Not approved',
};

/**
 * Tones say what the operator should do, not how good the news is. Changed and Partly approved need a
 * person (warning). Not approved is the ordinary forecast case, so it stays neutral rather than red.
 * Auto-approved is brand, distinct from a person's approval (success): the line's policy stands in for one.
 */
const TONES: Record<TaskApprovalStatus, TaskApprovalTone> = {
    [TaskApprovalStatus.Approved]: 'success',
    [TaskApprovalStatus.AutoApproved]: 'brand',
    [TaskApprovalStatus.PartlyApproved]: 'warning',
    [TaskApprovalStatus.NeedsReview]: 'warning',
    [TaskApprovalStatus.NotApproved]: 'neutral',
};

const TOOLTIPS: Partial<Record<TaskApprovalStatus, string>> = {
    [TaskApprovalStatus.NeedsReview]: 'Changed since approval',
};

/** API badge severity per tone; `info` is the blue badge, so Auto-approved reads the same as the web's brand pill. */
const SEVERITIES: Record<TaskApprovalTone, NonNullable<MetadataEnumTypeData['severity']>> = {
    neutral: 'neutral',
    warning: 'warning',
    brand: 'info',
    success: 'success',
};

/**
 * True when the whole Visit is approved, by a person or the line's auto-approve policy: its Job holds every
 * task. Boards show it as solid (not tentative), it reaches technicians and counts towards planned load.
 */
export function isFullyApproved(status: Standing): boolean {

    return FULLY_APPROVED.has(status);
}

/**
 * True when the stamper and Job construction may build a Job. Adds Partly approved to `isFullyApproved`:
 * its approved tasks become the Job and the builder splits the rest into a sibling Visit first.
 */
export function buildsJob(status: Standing): boolean {

    return BUILDS_JOB.has(status);
}

/**
 * True when a Job is built on a person's approval (Manual authority) rather than the line's policy:
 * the `buildsJob` standings less Auto-approved. Construction evidence and claims record it as Manual.
 */
export function buildsOnPersonApproval(status: Standing): boolean {

    return BUILDS_ON_PERSON_APPROVAL.has(status);
}

/**
 * True when a person approved some of the work, even if it changed since. Only a stamped (stored) row can
 * hold it; reschedule offers "Keep approved" for it, and Remove approval can withdraw it. An auto-approved
 * line re-approves itself, so Auto-approved is not one.
 */
export function heldByPerson(status: Standing): boolean {

    return HELD_BY_PERSON.has(status);
}

/**
 * A stamped task row's own standing: Approved while its stamp matches its revision, else NeedsReview
 * (changed since approval). Undefined for an unstamped row, whose standing each caller takes from the
 * line policy or leaves absent. The upcoming approval join, Visit browse and the not-in-Job list call it.
 */
export function rowApprovalStatus(row: TaskApprovalSummaryRow): TaskApprovalStatus.Approved | TaskApprovalStatus.NeedsReview | undefined {

    if (row.approval == null) return undefined;

    return isApprovalCurrent(row) ? TaskApprovalStatus.Approved : TaskApprovalStatus.NeedsReview;
}

/**
 * Who authorises a Job built from this standing: Manual for a person's approval (`buildsOnPersonApproval`),
 * AutoApproved for the line's policy, undefined when neither stands behind it. The Visit Job builder,
 * the construction claim and construction evidence call it; each decides what undefined means for it.
 */
export function constructionAuthority(status: Standing): JobConstructionAuthorisationKind.Manual | JobConstructionAuthorisationKind.AutoApproved | undefined {

    if (buildsOnPersonApproval(status)) return JobConstructionAuthorisationKind.Manual;
    if (status === TaskApprovalStatus.AutoApproved) return JobConstructionAuthorisationKind.AutoApproved;

    return undefined;
}

/** The one display name of a standing, on every board, tag, trail and API badge. */
export function approvalLabel(status: TaskApprovalStatus): string {

    return LABELS[status];
}

/** The one semantic colour of a standing; the web uses it as a `StatusTone`. */
export function approvalTone(status: TaskApprovalStatus): TaskApprovalTone {

    return TONES[status];
}

/** Hover text where the short label needs its full meaning; undefined for the self-explanatory standings. */
export function approvalTooltip(status: TaskApprovalStatus): string | undefined {

    return TOOLTIPS[status];
}

/** Enum badge options for every API approval field, from the same labels and tones as the web tags. */
export const TASK_APPROVAL_OPTIONS: MetadataEnumTypeData[] = [
    TaskApprovalStatus.Approved,
    TaskApprovalStatus.AutoApproved,
    TaskApprovalStatus.PartlyApproved,
    TaskApprovalStatus.NeedsReview,
    TaskApprovalStatus.NotApproved,
].map(status => ({
    key: approvalLabel(status),
    value: status,
    severity: SEVERITIES[approvalTone(status)],
}));
